import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react";
import apiConfig from "../../config/apiConfig";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useParams } from "react-router-dom";
import { apiFetch } from "../../apis/apiFetch";
import { useRaspStore } from "../../store/raspStore";
import { LoginContext } from "../../context/LoginContext";
import { usePermaddressViewModel } from "../../viewModels/usePermaddressViewModel";
import "./FormStyles.css";
import { validate } from "../../utils/validator";
import { permAddressValidationRules } from "../../validators/applicantValidator";

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

export interface PermaddressFormRef {
  save: () => Promise<Record<string, string>>;
  getErrors: () => Record<string, string>;
  setDataFromCurrentAddress: (data: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
  }) => void;
}

type CreateApplicantProps = {
  dataToSave: any;
  setDataToSave: React.Dispatch<React.SetStateAction<any>>;
};

// getCookie removed - using apiFetch

const CreatePermaddress = forwardRef<PermaddressFormRef, CreateApplicantProps>((props, ref) => {
  const { dataToSave, setDataToSave } = props;
  const { user } = useContext(LoginContext); // logged-in user

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [showToast, setShowToast] = useState<any>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );
  const regex = /^(g_|archived|extra_data)/;
  const apiUrl = apiConfig.getResourceUrl("applications");
  const metadataUrl = apiConfig.getResourceMetaDataUrl("applications");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const hasPrePopulated = useRef(false);
  const queryClient = useQueryClient();

  // userId is now managed via LoginContext

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    // dataToSave,
    // setDataToSave,
    loadMetadata,
    save,
  } = usePermaddressViewModel(user?.userId || "", appId || "");

  const [countriesList, setCountriesList] = useState<string[]>([]);
  const [statesList, setStatesList] = useState<string[]>([]);

  // Fetch all countries on load
  useEffect(() => {
    fetch("https://countriesnow.space/api/v0.1/countries")
      .then((res) => res.json())
      .then((data) => {
        if (!data.error) {
          const names = data.data.map((item: any) => item.country);
          setCountriesList(names);
        }
      })
      .catch((err) => console.error("Error fetching countries:", err));
  }, []);

  // Fetch states when a country is selected
  useEffect(() => {
    if (dataToSave["p_country"]) {
      fetch("https://countriesnow.space/api/v0.1/countries/states", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country: dataToSave["p_country"] }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (!data.error) {
            const names = data.data.states.map((item: any) => item.name);
            setStatesList(names);
          } else {
            setStatesList([]);
          }
        })
        .catch((err) => console.error("Error fetching states:", err));
    } else {
      setStatesList([]);
    }
  }, [dataToSave["p_country"]]);

  useEffect(() => {
    loadMetadata();
  }, []);

  // Auto-set user_id from logged-in user's email
  useEffect(() => {
    if (!user || !user.email_id) return;
    const users = foreignKeyData["Users"] || [];
    if (!users.length) return;

    const match = users.find((u: any) => {
      const userEmail = (
        u.email ||
        u.email_id ||
        u.user_email ||
        ""
      ).toLowerCase();
      return userEmail === user.email_id.toLowerCase();
    });

    if (match && match.id) {
      setDataToSave((prev: any) => ({
        ...prev,
        user_id: match.id,
      }));
    } else {
      console.warn(
        "No matching user found for logged-in user email:",
        user.email_id,
      );
    }
  }, [user, foreignKeyData]);

  // async function, not useQuery
  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string,
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  // async function, not useQuery
  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  // useQuery only here
  const {
    data: metaData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["resMetaData", "permaddressCreate"],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter(
        (field: any) => field.foreign,
      );
      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ["foreignData", field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(
            field.foreign,
            field.name,
            field.foreign_field,
          );
        }
      }

      const enumFields = data[0].fieldValues.filter(
        (field: any) => field.isEnum === true,
      );
      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ["enum", field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });

  useEffect(() => {
    console.log("data to save", dataToSave);
  }, [dataToSave]);

  const {
    data: dataRes,
    isLoading: isLoadingDataRes,
    error: errorDataRes,
  } = useQuery({
    queryKey: ["resourceData", "permaddressCreate", user?.userId, localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      if (!user?.userId) return [];
      const response = await apiFetch(
        `${apiConfig.getResourceUrl("applications")}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return data.resource || [];
    },
    enabled: !!user?.userId,
  });
  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (dataRes && activeDriveId) {
      const activeRecord = dataRes.find((r: any) => r.drive_id === activeDriveId);
      let dataToStore = { resource: "permaddress", records: activeRecord ? [activeRecord] : [] };
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
    }
  }, [dataRes, user]);

  // Pre-populate form with existing data when user_id is resolved and records are loaded
  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (
      !hasPrePopulated.current &&
      dataRes &&
      dataRes.length > 0 &&
      dataToSave.user_id &&
      activeDriveId
    ) {
      const userRecord = dataRes.find(
        (r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId,
      );
      if (userRecord) {
        hasPrePopulated.current = true;
        // Merge userRecord into dataToSave, ensuring id and drive_id are preserved
        setDataToSave((prev: any) => ({ ...userRecord, ...prev }));
      }
    }
  }, [dataRes, dataToSave.user_id]);

  const handleCreate = async () => {
    const vErrors = validate(dataToSave, permAddressValidationRules);
    setErrors(vErrors);

    // Always save as draft regardless of validation for better UX
    await save(dataToSave);
    queryClient.invalidateQueries({
      queryKey: ["resourceData", "permaddressCreate"],
    });
    
    return vErrors;
  };

  // Expose save method and setDataFromCurrentAddress method to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleCreate,
    getErrors: () => errors,
    setDataFromCurrentAddress: (data: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      country: string;
      pincode: string;
    }) => {
      setDataToSave((prev: any) => ({
        ...prev,
        p_line1: data.line1,
        p_line2: data.line2,
        p_city: data.city,
        p_country: data.country,
        p_pincode: data.pincode,
        p_state: "",
      }));
      setTimeout(() => {
        setDataToSave((prev: any) => ({
          ...prev,
          p_state: data.state,
        }));
      }, 200); // wait for statesList load
    },
  }));

  const handleSearchChange = (fieldName: string, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [fieldName]: value }));
  };

  return (
    <div className="form-container">
      <h3 className="form-subtitle">Permanent Address</h3>

      <div className="form-grid">
        {/* Line 1 */}
        <div className="form-group">
          <label className="form-label">Line 1 *</label>
          <input
            type="text"
            className={`form-input ${errors.p_line1 ? "input-error" : ""}`}
            name="line1"
            required
            value={dataToSave["p_line1"] || ""}
            placeholder="Line 1"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, p_line1: e.target.value }));
              if (errors.p_line1) setErrors(prev => ({ ...prev, p_line1: "" }));
            }}
          />
          {errors.p_line1 && <span className="error-text">{errors.p_line1}</span>}
        </div>

        {/* Line 2 */}
        <div className="form-group">
          <label className="form-label">Line 2 *</label>
          <input
            type="text"
            className={`form-input ${errors.p_line2 ? "input-error" : ""}`}
            name="line2"
            required
            value={dataToSave["p_line2"] || ""}
            placeholder="Line 2"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, p_line2: e.target.value }));
              if (errors.p_line2) setErrors(prev => ({ ...prev, p_line2: "" }));
            }}
          />
          {errors.p_line2 && <span className="error-text">{errors.p_line2}</span>}
        </div>

        {/* Country */}
        <div className="form-group">
          <label className="form-label">Country *</label>
          <select
            className={`form-select ${errors.p_country ? "input-error" : ""}`}
            name="country"
            required
            value={dataToSave["p_country"] || ""}
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, p_country: e.target.value }));
              if (errors.p_country) setErrors(prev => ({ ...prev, p_country: "" }));
            }}
          >
            <option value="">Select Country</option>
            {countriesList.map((val: any, idx: any) => (
              <option key={idx} value={val}>
                {val}
              </option>
            ))}
          </select>
          {errors.p_country && <span className="error-text">{errors.p_country}</span>}
        </div>

        {/* State */}
        <div className="form-group">
          <label className="form-label">State *</label>
          <select
            className={`form-select ${errors.p_state ? "input-error" : ""}`}
            name="state"
            required
            value={dataToSave["p_state"] || ""}
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, p_state: e.target.value }));
              if (errors.p_state) setErrors(prev => ({ ...prev, p_state: "" }));
            }}
          >
            <option value="">Select State</option>
            {statesList.map((val: any, idx: any) => (
              <option key={idx} value={val}>
                {val}
              </option>
            ))}
          </select>
          {errors.p_state && <span className="error-text">{errors.p_state}</span>}
        </div>

        {/* City */}
        <div className="form-group">
          <label className="form-label">City *</label>
          <input
            type="text"
            className={`form-input ${errors.p_city ? "input-error" : ""}`}
            name="city"
            required
            value={dataToSave["p_city"] || ""}
            placeholder="City"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, p_city: e.target.value }));
              if (errors.p_city) setErrors(prev => ({ ...prev, p_city: "" }));
            }}
          />
          {errors.p_city && <span className="error-text">{errors.p_city}</span>}
        </div>

        {/* Pincode */}
        <div className="form-group">
          <label className="form-label">Pincode *</label>
          <input
            type="text"
            className={`form-input ${errors.p_pincode ? "input-error" : ""}`}
            name="pincode"
            required
            value={dataToSave["p_pincode"] || ""}
            placeholder="Pincode"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, p_pincode: e.target.value }));
              if (errors.p_pincode) setErrors(prev => ({ ...prev, p_pincode: "" }));
            }}
          />
          {errors.p_pincode && <span className="error-text">{errors.p_pincode}</span>}
        </div>

        {/* User ID - Auto-populated from logged-in user, hidden from UI */}
        <input type="hidden" name="user_id" value={dataToSave.user_id || ""} />
      </div>
    </div>
  );
});

export default CreatePermaddress;